import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../assets/styles/waitingListLead/WaitingListLeadOverview.scss";
import EmailModal from "./EmailModal";
import NoteModal from "./NoteModal";
import WaitingListLeadStateClosed from "./WaitingListLeadStateClosedV1";
import { WaitingListLeadState } from "../types/enums";
import ApiError from "../../../../lib/ApiError";
import BackToTopButton from "../../../../components/ui/buttons/BackToTopButton";
import WaitingListLeadStateAcquiredV1 from "./WaitingListLeadStateAcquiredV1";
import WaitingListLeadStateOpenV1 from "./WaitingListLeadStateOpenV1";
import WaitingListLeadStateRejectedV1 from "./WaitingListLeadStateRejectedV1";
import WaitingListLeadStateAcquiredWithClosedDateV1 from "./WaitingListLeadStateAcquiredWithClosedDateV1";
import { useSupplierInfo } from "layout/context/SupplierInfoContext";
import { isRegistrationForAm } from "constants/index";
import { getCommaSeparatedPhoneNumberFromArray } from "lib/leadNumber/phoneNumber";
import WaitingListLeadBasicInformationTableV1 from "./WaitingListLeadBasicInformationTableV1";
import WaitingListLeadProductCriteriaInformationTableV1 from "./WaitingListLeadProductCriteriaInformationTableV1";
import GroupSection from "./GroupSection";
import ProductDetailCard from "./ProductDetailCard";
import ProductDetailCardNode from "./ProductDetailCardNode";
import { ILeadPhoneNumber } from "types";
import CollapsibleGroup from "./CollapsibleGroup";
import CallModal from "./CallModal";
import TaskModal from "./TaskModal";
import SourceModal from "./SourceModal"; // Import der SourceModal
import GroupSectionBlurry from "./GroupSectionBlurry";
// LockedViewCSS
import "./LockedView.scss";

// Hilfskomponente für "blurry" Daten mit Schloss-Icon und Tooltip
const BlurredData = ({ text }: { text: string }) => {
  return (
    <div className="blurred-data">
      <span className="blurred" title="Diese Daten jetzt freischalten">
        {text}
      </span>
      <i className="fa-solid fa-lock" />
    </div>
  );
};

// Definition eines Activity-Interfaces
interface Activity {
  icon: string;
  time: string;
  text: string;
  title?: string;
  detail?: string;
}

const mockData = {
  waitingListLeadDetailsInfo: {
    firstName: "Max",
    lastName: "Mustermann",
    clientAvailability: "Mo-Fr 9-17 Uhr",
    bedarfsort: "Mainz, 55116",
    addresse: "Musterstraße 1, 55116 Mainz",
    phoneNumber: "0123456789",
    phoneNumbers: [
      { type: "mobile", number: "0123456789", internationalNoBlanks: 176826378123 },
    ] as unknown as ILeadPhoneNumber[],
    waitingListLeadState: WaitingListLeadState.Acquired,
    isClosedByClient: false,
    isPersonalProduct: false,
    productName: "Pflegedienst",
    requestRegion: "Mainz",
    requestZipCode: "55116",
    // Angepasste Kriterien mit jeweils nur einer Option
    productCriteriaList: [
      { criteriaName: "Pflegegrad/-stufe", value: "3" },
      { criteriaName: "Aufgaben", value: "Grundpflege + Behandlungspflege" },
      { criteriaName: "Betreuungsbedarf – Wöchentlicher Umfang", value: "Täglich" },
      { criteriaName: "Zeitpunkt – Bedarf", value: "Schnellstmöglich" }
    ],
    waitingListLeadNumber: "ANF-01"
  },
  productItems: [
    {
      title: "Pflegedienst",
      detail: "Status: Aktiv",
      onOptionsClick: () => alert("Optionen für Produkt A")
    }
  ],
  relativeItems: [
    {
      title: "Petra Mustermann",
      detail: "Mutter - Primärer Kontakt",
      onOptionsClick: () => alert("Optionen für Mutter")
    },
    {
      title: "Hans Mustermann",
      detail: "Bruder - Sekundärer Kontakt",
      onOptionsClick: () => alert("Optionen für Onkel")
    }
  ],
  tabContent: {
    activities: [
      { icon: "fa-solid fa-check", time: "02.04.2025, 08:00", text: "Anfrage freischalten", detail: "Anruf erfolgreich" },
      { icon: "fa-solid fa-star", time: "02.04.2025, 07:00", text: "Anfrage erhalten", detail: "Anruf erfolgreich" },
    ],
    appointments: { title: "Termine", text: "Hier werden alle geplanten Termine angezeigt." },
    contactDetailsTitle: "Kontaktdetails"
  }
};

interface ContactInfo {
  firstName: string;
  lastName: string;
  clientAvailability: string;
  phoneNumbers: ILeadPhoneNumber[];
  email?: string;
}

interface MiddleSectionProps {
  contactInfo: ContactInfo | null;
  activityLog: Activity[];
  emailActivities: Activity[];
  noteActivities: Activity[];
  callActivities: Activity[];
  taskActivities: Activity[];
  showPromotionModal: () => void;
  getWaitingListLeadStatusView: (
    state: WaitingListLeadState,
    isClosedByClient: boolean,
    isPersonalProduct: boolean
  ) => JSX.Element;
}

const MiddleSection: React.FC<MiddleSectionProps> = ({
  contactInfo,
  activityLog,
  emailActivities,
  noteActivities,
  callActivities,
  taskActivities,
  getWaitingListLeadStatusView,
  showPromotionModal
}) => {
  const [activeTab, setActiveTab] = useState("aktivität");
  const navigate = useNavigate();

  // Erweiterte Tab-Liste um den "Kriterien"-Tab
  const tabs = ["aktivität", "anrufe", "e-Mails", "notizen", "aufgaben"];

  // Funktion, die beim Klick auf "Freischalten" ausgeführt wird
  const handleUnlock = () => {
    navigate("/waiting-list-lead-details");
  };

  const handleReject = () => {
    // Hier kannst Du die Ablehnungslogik implementieren
    alert("Anfrage abgelehnt");
  };

  return (
    <div className="middle-section">

      {/* Informationsbanner zur neuen Anfrage */}
      <WaitingListLeadStateOpenV1
        secureId={""}
        isPersonalProduct={false}
        onSuccessCallback={() => setTimeout(() => showPromotionModal(), 2000)}
      />
      <div className="criteria-table">
        <h2>Produktkriterien: Pflegedienst</h2>
        <table>
          <thead>
            <tr>
              <th>Kriterium</th>
              <th>Ausgewählte Option</th>
            </tr>
          </thead>
          <tbody>
            {mockData.waitingListLeadDetailsInfo.productCriteriaList.map((criteria, index) => (
              <tr key={index}>
                <td>{criteria.criteriaName}</td>
                <td>{criteria.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      <div className="tab-content">
        {activeTab === "aktivität" && (
          <div className="activities">
            <h2>Aktivitäten</h2>
            {activityLog
              .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
              .map((activity, index) => (
                <div key={index} className="activity">
                  <i className={activity.icon} /> {activity.time} - {activity.text}
                </div>
              ))}
          </div>
        )}
        {activeTab === "anrufe" && (
          <div className="calls">
            <h2>Anrufe</h2>
            {callActivities.length > 0 ? (
              callActivities
                .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                .map((activity, index) => (
                  <div key={index} className="activity">
                    <i className={activity.icon} /> {activity.time} - {activity.text}
                  </div>
                ))
            ) : (
              <p>Keine Anrufe vorhanden.</p>
            )}
          </div>
        )}
        {activeTab === "e-Mails" && (
          <div className="emails">
            <h2>E‑Mails</h2>
            {emailActivities.length > 0 ? (
              emailActivities
                .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                .map((activity, index) => (
                  <div key={index} className="activity">
                    <i className={activity.icon} /> {activity.time} - {activity.text}
                  </div>
                ))
            ) : (
              <p>Keine E‑Mails vorhanden.</p>
            )}
          </div>
        )}
        {activeTab === "notizen" && (
          <div className="notes">
            <h2>Notizen</h2>
            {noteActivities.length > 0 ? (
              noteActivities
                .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                .map((activity, index) => (
                  <div key={index} className="activity">
                    <i className={activity.icon} /> {activity.time} - {activity.text}
                  </div>
                ))
            ) : (
              <p>Keine Notizen vorhanden.</p>
            )}
          </div>
        )}
        {activeTab === "aufgaben" && (
          <div className="tasks">
            <h2>Aufgaben</h2>
            {taskActivities.length > 0 ? (
              taskActivities
                .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                .map((activity, index) => (
                  <div key={index} className="activity">
                    <i className={activity.icon} /> {activity.time} - {activity.text}
                  </div>
                ))
            ) : (
              <p>Keine Aufgaben vorhanden.</p>
            )}
          </div>
        )}
        {activeTab === "termine" && (
          <div className="appointments">
            <h2>{mockData.tabContent.appointments.title}</h2>
            <p>{mockData.tabContent.appointments.text}</p>
          </div>
        )}
        {activeTab === "kontaktdetails" && (
          <div className="contact-details-tab">
            <h2>{mockData.tabContent.contactDetailsTitle}</h2>
            {mockData.waitingListLeadDetailsInfo && (
              <>
                <WaitingListLeadBasicInformationTableV1
                  firstName={mockData.waitingListLeadDetailsInfo.firstName}
                  lastName={mockData.waitingListLeadDetailsInfo.lastName}
                  productName={mockData.waitingListLeadDetailsInfo.productName}
                  clientAvailability={mockData.waitingListLeadDetailsInfo.clientAvailability}
                  waitingListLeadState={mockData.waitingListLeadDetailsInfo.waitingListLeadState}
                  requestRegion={mockData.waitingListLeadDetailsInfo.requestRegion}
                  requestZipCode={mockData.waitingListLeadDetailsInfo.requestZipCode}
                  phoneNumber={getCommaSeparatedPhoneNumberFromArray(
                    mockData.waitingListLeadDetailsInfo.phoneNumbers
                  )}
                  isClosedByClient={mockData.waitingListLeadDetailsInfo.isClosedByClient}
                  isPersonalProduct={mockData.waitingListLeadDetailsInfo.isPersonalProduct}
                />
                <WaitingListLeadProductCriteriaInformationTableV1
                  waitingListLeadCriteriaInfo={{
                    waitingListLeadState: mockData.waitingListLeadDetailsInfo.waitingListLeadState,
                    isClosedByClient: mockData.waitingListLeadDetailsInfo.isClosedByClient,
                    isPersonalProduct: mockData.waitingListLeadDetailsInfo.isPersonalProduct,
                    productCriteriaList: mockData.waitingListLeadDetailsInfo.productCriteriaList
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function WaitingListLeadOverviewV1() {
  const { secureId } = useParams();
  const { isAuthenticated } = useSupplierInfo();
  const [isRegisterForAMSet, setIsRegisterForAMSet] = useState(false);
  const [promotionModal, setPromotionModal] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const navigate = useNavigate();

  const [activityLog, setActivityLog] = useState<Activity[]>(mockData.tabContent.activities);
  const [emailActivities, setEmailActivities] = useState<Activity[]>([]);
  const [noteActivities, setNoteActivities] = useState<Activity[]>([]);
  const [callActivities, setCallActivities] = useState<Activity[]>([]);
  const [taskActivities, setTaskActivities] = useState<Activity[]>([]);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);

  useEffect(() => {
    const isSet = localStorage.getItem(isRegistrationForAm) !== null;
    setIsRegisterForAMSet(isSet);
  }, [navigate]);

  function getWaitingListLeadStatusView(
    state: WaitingListLeadState,
    isClosedByClient: boolean,
    isPersonalProduct: boolean
  ) {
    if (isClosedByClient) {
      if (state === WaitingListLeadState.Acquired) {
        return <WaitingListLeadStateAcquiredWithClosedDateV1 isPersonalProduct={isPersonalProduct} />;
      }
      return <WaitingListLeadStateClosed isPersonalProduct={isPersonalProduct} />;
    }
    switch (state) {
      case WaitingListLeadState.Open:
        return (
          <WaitingListLeadStateOpenV1
            secureId={secureId || ""}
            isPersonalProduct={isPersonalProduct}
            onSuccessCallback={() => setTimeout(() => showPromotionModal(), 2000)}
          />
        );
      case WaitingListLeadState.Acquired:
        return <WaitingListLeadStateAcquiredV1 isPersonalProduct={isPersonalProduct} />;
      default:
        return <WaitingListLeadStateRejectedV1 />;
    }
  }

  function showPromotionModal() {
    if (!isRegisterForAMSet && !isAuthenticated) {
      setPromotionModal(true);
    }
  }

  const handleEmailSentActivity = (activity: Activity) => {
    setEmailActivities(prev => [activity, ...prev]);
    setActivityLog(prev => [activity, ...prev]);
    setTimeout(() => {
      const simulatedResponse: Activity = {
        icon: "fa-solid fa-reply",
        time: new Date().toLocaleTimeString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }),
        detail: new Date().toLocaleTimeString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }),
        title: "Antwort vom Klienten",
        text: "Antwort vom Klienten: Ihre Nachricht wurde gelesen und wird in Kürze beantwortet."
      };
      setEmailActivities(prev => [simulatedResponse, ...prev]);
      setActivityLog(prev => [simulatedResponse, ...prev]);
    }, 5000);
  };

  const modalOrder = ["call", "email", "note", "task"];
  const gap = 20;
  const startLeft = 50;

  const calculateModalOffsets = () => {
    let currentLeft = startLeft;
    const offsets: { [key: string]: number } = {};

    modalOrder.forEach((modalKey) => {
      let isOpen = false;
      switch (modalKey) {
        case "call":
          isOpen = callModalOpen;
          break;
        case "email":
          isOpen = emailModalOpen;
          break;
        case "note":
          isOpen = noteModalOpen;
          break;
        case "task":
          isOpen = taskModalOpen;
          break;
        default:
          break;
      }
      const width = isOpen ? 500 : 250;
      offsets[modalKey] = currentLeft;
      currentLeft += width + gap;
    });
    return offsets;
  };

  const modalOffsets = calculateModalOffsets();

  return (
    <div className="lead-detail-container">
      <BackToTopButton />
      <div className="detail-layout">
        <div className="sidebar-left">
          {mockData.waitingListLeadDetailsInfo && (
            <>
              <div className="contact-details">
                <div className="contact-info">
                  <div className="contact-info-header">
                    <span onClick={() => navigate("/leadOverview")}>
                      <i className="fa-solid fa-arrow-left" /> Zurück
                    </span>
                    <span
                      className="contact-info-header-title"
                      onClick={() => setSourceModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      Quelle: VP Verbund Pflegehilfe
                    </span>
                  </div>
                  <div 
                  style={{display: "flex", alignItems: "center"}}>
                  
                    <h2><BlurredData text={mockData.waitingListLeadDetailsInfo.firstName} />{" "}</h2>
                  <h2
                  style={{marginLeft: "15px"}}>  <BlurredData text={mockData.waitingListLeadDetailsInfo.lastName} /></h2>
                  </div>
                  <p>Erreichbar: {mockData.waitingListLeadDetailsInfo.clientAvailability} </p>
                  <p>Interessiert an: Pflegedienst</p>
                </div>
                {/* Gesperrte Aktionsbuttons */}
                <div className="action-buttons">
                  <button disabled onClick={() => setCallModalOpen(true)}>
                    <i className="fa-solid fa-phone" />
                  </button>
                  <button disabled onClick={() => setEmailModalOpen(true)}>
                    <i className="fa-solid fa-envelope" />
                  </button>
                  <button disabled onClick={() => setNoteModalOpen(true)}>
                    <i className="fa-solid fa-sticky-note" />
                  </button>
                  <button disabled onClick={() => setTaskModalOpen(true)}>
                    <i className="fa-solid fa-check" />
                  </button>
                </div>
              </div>
              <CollapsibleGroup title="Über Ihren Klienten">
                <ProductDetailCardNode
                  type="text"
                  title="Klientenname"
                  content={
                    <>
                      <BlurredData text={mockData.waitingListLeadDetailsInfo.lastName} />
                    </>
                  }
                />
                <ProductDetailCardNode
                  type="editabletext"
                  title="Verfügbarkeit"
                  content={mockData.waitingListLeadDetailsInfo.clientAvailability}
                />
                <ProductDetailCardNode
                  type="text"
                  title="Bedarfsort"
                  content={mockData.waitingListLeadDetailsInfo.bedarfsort}
                />
                <ProductDetailCardNode
                  type="text"
                  title="Adresse"
                  content={
                    <div style={{ display: "flex" }}>
                      Musterstraße
                      <div className="blurred-data">
                        <span className="blurred" title="Diese Daten jetzt freischalten">
                          14
                        </span>
                        <i className="fa-solid fa-lock" />
                      </div>
                    </div>
                  }
                />
                <ProductDetailCardNode
                  type="text"
                  title="Telefonnummer"
                  content={<BlurredData text={mockData.waitingListLeadDetailsInfo.phoneNumber} />}
                />
              </CollapsibleGroup>

              <CollapsibleGroup title="Produktkriterien">
                <ProductDetailCard
                  type="text"
                  title="Produktname"
                  content={"Pflegedienst"}
                />
                <ProductDetailCard
                  type="text"
                  title="Region"
                  content={mockData.waitingListLeadDetailsInfo.requestRegion}
                />
                <ProductDetailCard
                  type="text"
                  title="PLZ"
                  content={mockData.waitingListLeadDetailsInfo.requestZipCode}
                />
                {mockData.waitingListLeadDetailsInfo.productCriteriaList &&
                  mockData.waitingListLeadDetailsInfo.productCriteriaList.map((criteria, index) => (
                    <ProductDetailCard
                      type="text"
                      key={index}
                      title={criteria.criteriaName}
                      content={criteria.value}
                    />
                  ))}
              </CollapsibleGroup>
              <CollapsibleGroup title="Über die Quelle">
                <ProductDetailCard
                  type="text"
                  title="VP Verbund Pflegehilfe"
                  content="Deutschlands größte Pflegeberatung"
                />
              </CollapsibleGroup>

            </>
          )}
        </div>
        <div className="main-content">
          <div className="main-content-header">
            <div className="main-content-header">
              <i className="fa-solid fa-arrow-left" />
              Vorheriger
            </div>
            <h1 className="detail-number">
              Anfrage: {mockData.waitingListLeadDetailsInfo.waitingListLeadNumber}
            </h1>
            <div className="main-content-header">
              Nächster
              <i className="fa-solid fa-arrow-right" />
            </div>
          </div>

          <MiddleSection
            getWaitingListLeadStatusView={getWaitingListLeadStatusView}
            contactInfo={{
              firstName: mockData.waitingListLeadDetailsInfo.firstName,
              lastName: mockData.waitingListLeadDetailsInfo.lastName,
              clientAvailability: mockData.waitingListLeadDetailsInfo.clientAvailability,
              phoneNumbers: mockData.waitingListLeadDetailsInfo.phoneNumbers,
              email: "test@mail.de"
            }}
            activityLog={activityLog}
            emailActivities={emailActivities}
            noteActivities={noteActivities}
            callActivities={callActivities}
            taskActivities={taskActivities}
            showPromotionModal={showPromotionModal}
          />
        </div>
        <div className="sidebar-right">
          <GroupSectionBlurry title="Angehörige" items={mockData.relativeItems} />
          <GroupSection title="Anfragen" items={mockData.productItems} />
          {emailActivities.length > 0 && (
            <GroupSection title="Gesendete E‑Mails" items={emailActivities} />
          )}
          {taskActivities.length > 0 && (
            <GroupSection title="Geplante Aufgaben" items={taskActivities} />
          )}
        </div>
      </div>
      <SourceModal
        open={sourceModalOpen}
        onClose={() => setSourceModalOpen(false)}
      />
    </div>
  );
}

export default WaitingListLeadOverviewV1;
